import { IField } from '@frontend/app/containers/Projects/LandingPages/types';
import { TProject } from '@frontend/app/containers/Projects/types';
import { MemberSearchQuery_members } from '@frontend/app/queries/types/MemberSearchQuery';
import { ApplicationFormFieldsInput } from '@frontend/app/types/globalTypes';

export interface ApplicationField {
  id: string;
  label: string;
  value: string | null;
  required: boolean;
  type: 'text' | 'email' | 'number' | 'file' | 'select' | 'textarea';
  placeholder?: string;
}

export interface ApplicationFieldsData {
  fields: ApplicationField[];
}

export const adaptMemberApplicationFields = (
  member: MemberSearchQuery_members | null,
  project: TProject | null
): ApplicationFieldsData => {
  if (!member || !project) {
    return { fields: [] };
  }

  const memberFields = member.fields || {};
  const fields: ApplicationField[] = [];

  // Get union of all onboarding template fields
  const allTemplateFields = new Map<string, Partial<IField>>();

  if (project.onboardingTemplates) {
    project.onboardingTemplates.forEach((template) => {
      // Extract fields from different template configurations
      const extractFields = (formFields: ApplicationFormFieldsInput) => {
        if (formFields?.memberFieldSchemas) {
          formFields.memberFieldSchemas.forEach((field) => {
            allTemplateFields.set(field.schemaId?.toString() || field.label, {
              id: field.schemaId,
              label: field.label,
              required: field.required,
              order: field.order,
              type: 'text', // Default type, could be enhanced
            });
          });
        }
        if (formFields?.dbColumns) {
          formFields.dbColumns.forEach((field) => {
            allTemplateFields.set(field.name, {
              label: field.label || field.name,
              required: field.required,
              order: field.order,
              name: field.name,
              dbName: field.name,
              type: 'text', // Default type
            });
          });
        }
      };

      // Extract from different template types and pages
      if (template.config?.pulseTemplate?.pages) {
        template.config.pulseTemplate.pages.forEach((page) => {
          if (page.hero?.applicationFormFields) {
            extractFields(page.hero.applicationFormFields);
          }
          if (page.forms?.applicationFormFields) {
            extractFields(page.forms.applicationFormFields);
          }
        });
      }

      if (template.config?.amplifyTemplate?.pages) {
        template.config.amplifyTemplate.pages.forEach((page) => {
          if (page.home?.forms?.applicationFormFields) {
            extractFields(page.home.forms.applicationFormFields);
          }
          if (page.forms?.applicationFormFields) {
            extractFields(page.forms.applicationFormFields);
          }
        });
      }
    });
  }

  // Create application fields from template definitions
  allTemplateFields.forEach((templateField, fieldId) => {
    let value = memberFields[fieldId] || memberFields[templateField.name] || null;

    // Special handling for common fields that might not be in memberFields
    if (!value) {
      const labelLower = (templateField.label || '').toLowerCase();
      if (labelLower.includes('email')) {
        value = member.email;
      }
    }

    fields.push({
      id: fieldId,
      label: templateField.label || templateField.name || fieldId,
      value: value ? String(value) : null,
      required: templateField.required || false,
      type: getFieldType(templateField.type),
    });
  });

  // Sort fields by order if available, otherwise maintain original order
  fields.sort((a, b) => {
    const aTemplateField = allTemplateFields.get(a.id);
    const bTemplateField = allTemplateFields.get(b.id);

    const aOrder = aTemplateField?.order ?? 999;
    const bOrder = bTemplateField?.order ?? 999;

    return aOrder - bOrder;
  });

  return { fields };
};

const getFieldType = (templateType: string): ApplicationField['type'] => {
  switch (templateType?.toLowerCase()) {
    case 'email':
      return 'email';
    case 'number':
    case 'currency':
      return 'number';
    case 'file':
    case 'upload':
      return 'file';
    case 'select':
    case 'dropdown':
      return 'select';
    case 'textarea':
    case 'text_area':
      return 'textarea';
    default:
      return 'text';
  }
};
